import { ChangeEvent, FC, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import EditModal from 'components/UI/EditModal';
import Button from 'components/UI/Button';
import { Content, ButtonGrid } from './styled';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import InputField from 'components/UI/InputField';
import SelectField from 'components/UI/SelectField';
import Loader from 'components/UI/Loader';
import { useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import {
  AssignSoftwareAttribute,
  SelectableSoftware,
} from 'models/softwareUpate';
import {
  clearSelectableSoftwares,
  fetchSelectableSoftwares,
  selectableSoftwaresSelector,
} from 'store/softwareUpdateState/softwareUpdateState';
import { assignSoftwareToDevice } from 'store/softwareUpdateState/softwareUpdateState';
import Heading, { Type as HeadingType } from 'components/UI/Heading';

type Props = {
  iprId: string | undefined;
  open: boolean;
  onClose: () => void;
};

const AssignSoftwareModal: FC<Props> = ({ iprId, open, onClose }) => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm();
  const { softwares, isLoading }: SelectableSoftware = useAppSelector(
    selectableSoftwaresSelector
  );

  // Fetch softwares
  const fetchSoftwares = useCallback(
    (name: string) => {
      dispatch(fetchSelectableSoftwares(name));
    },
    [dispatch]
  );

  const clearSoftwares = useCallback(() => {
    dispatch(clearSelectableSoftwares());
  }, [dispatch]);

  const onInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value;
      if (input.length >= 3) {
        fetchSoftwares(input);
      } else {
        clearSoftwares();
      }
    },
    [fetchSoftwares, clearSoftwares]
  );

  const { id } = useParams();

  const loadingSpinner = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
  }, [isLoading]);

  const onSelect = useCallback(
    (name: AssignSoftwareAttribute) => (value: string) => setValue(name, value),
    [setValue]
  );

  // Submit
  const onSubmit: SubmitHandler<FieldValues> = useCallback(
    (values: FieldValues) => {
      const softwareId = values.software;
      if (id && softwareId) {
        dispatch(assignSoftwareToDevice(id, softwareId));
        onClose();
      }
    },
    [dispatch, id, onClose]
  );

  return (
    <EditModal title="software_update_create.title" open={open} close={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Content>
          <Heading type={HeadingType.h4}>
            <FormattedMessage id="software_update_create.sub_title" />
          </Heading>
          <InputField
            description="software_update_create.ipr_id"
            error={errors.iprId}
            defaultValue={iprId}
            disabled={true}
          />
          <SelectField
            name="software_update.software"
            enableSearch={true}
            error={errors.software}
            onSelect={onSelect(AssignSoftwareAttribute.SOFTWARE)}
            register={register(AssignSoftwareAttribute.SOFTWARE, {
              required: {
                value: true,
                message: 'input.required',
              },
            })}
            placeholder={intl.formatMessage({
              id: 'software_update_create.start_writing',
            })}
            label={intl.formatMessage({
              id: 'software_update_create.software',
            })}
            onInput={onInput}
            options={softwares || []}
          />
        </Content>
        <ButtonGrid>
          {loadingSpinner}
          <Button
            onClick={onClose}
            backgroundColor="transparent"
            color="primary"
          >
            <FormattedMessage id="modal.btn_close" />
          </Button>
          <Button submit>
            <FormattedMessage id="software_update_create.btn_save" />
          </Button>
        </ButtonGrid>
      </form>
    </EditModal>
  );
};

export default AssignSoftwareModal;
